import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import {
  DATE_ONLY_INPUT,
  INTEGER_INPUT,
  OPTIONAL_DATE_ONLY_INPUT,
} from "../../types/socketInputs";
import { QUANTITY_INPUT } from "./quantityInput";

/**
 * Upsert Purchase Order Line
 *
 * Takes ONE purchase-order LINE as single-value input sockets, with its
 * header's fields alongside, shaped after the Gently model
 * (`ap_internal_purchase_orders` + `ap_internal_purchase_order_line_items`) —
 * not after any source system. The service groups lines into orders and writes
 * both halves; a CDC observer or file parser maps its own columns into these
 * sockets, and anything source-specific (status codes, sentinels, vendor-code
 * prefixes) belongs in that upstream mapping.
 *
 * Flat, not nested, because that is the shape sources actually deliver: AS400
 * SUPTRANS, a CSV export and a CDC feed all emit one record per line with the
 * order's columns repeated on it.
 *
 * Half of the replacement for the retired `transform_suptrans_purchase_orders`
 * step, which read the `rexel_suptrans` landing table directly and decided per
 * row whether it was a supplier purchase or an internal transfer. On the
 * generic path that split is the WIRING: fan the same rows into this step and into
 * `upsert_transfer_order_line`, and each writes only the rows whose key
 * resolves for it — a vendor code that names a supplier lands here, one that
 * names a warehouse lands there. A code that resolves to BOTH would be written
 * twice, which is a genuine ambiguity in the source data worth fixing there.
 *
 * A leaf step: per-row outputs are execution results, not a downstream data
 * contract.
 */
export const upsertPurchaseOrderLineStep = defineStepConfig({
  type: "upsert_purchase_order_line",
  name: "Upsert Purchase Order Line",
  description:
    "Upserts one purchase-order line and its order header (order/line origin ids, supplier code, product SKU, dates, status, quantity, price). Resolves the supplier, warehouse and product, then writes both tables in source-agnostic batches. Fan a trigger's per-field array outputs into its single-value inputs and add a batch config to process rows in chunks.",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    // Nullable for the same reason as the other upsert steps: input is
    // validated BEFORE the handler runs, a parse failure is non-retryable, and
    // the batch path shares one activity per chunk — so a non-nullable socket
    // would fail a whole chunk of good rows over a few junk ones. A missing
    // value is simply a row this step reports as unwritable.
    orderOriginId: z
      .string()
      .nullable()
      .describe(
        "The ORDER's natural key in the source system; null is unresolvable",
      ),
    lineOriginId: z
      .string()
      .nullable()
      .describe(
        "The LINE's natural key, unique per line — not per order. Must distinguish lines an order repeats (AS400 reuses a line number across ship-from locations). Null is unresolvable.",
      ),
    supplierCode: z
      .string()
      .nullable()
      .describe(
        "Supplier origin code — `ap_suppliers.origin_id`, which the vendor ingest stamps division-scoped as `division:vendorCode`. Required: a row whose supplier does not resolve cannot be written. Compose the key upstream where the source's raw code differs.",
      ),
    shipToWarehouseCode: z
      .string()
      .nullable()
      .optional()
      .describe(
        "Warehouse origin code for the ship-to. Lands on the header (last row wins) AND on this line as its own destination — an order's lines can ship to different branches, and open supply is counted per warehouse. Optional: an unresolved code is reported and the order still lands without one.",
      ),
    productSku: z
      .string()
      .nullable()
      .describe(
        "Product SKU. An unknown or missing SKU does NOT drop the line — it lands with no product reference and is reported, so one bad item code cannot take an order's other lines down with it.",
      ),
    externalId: z
      .string()
      .nullable()
      .optional()
      .describe(
        "The order's human-facing number. Defaults to the order origin id. Note `external_id` is unique across ALL organizations, so a value that collides with another order under a different origin id fails the write chunk.",
      ),
    // A validated date rather than `z.string()`: the value reaches a DATE bind
    // directly and `order_date` is NOT NULL, so a malformed value would fail
    // the whole chunk's INSERT. DATE_ONLY_INPUT also accepts the
    // slash-separated spelling GAINS emits (`2026/07/08`).
    orderDate: DATE_ONLY_INPUT.nullable().describe(
      "Order date, YYYY-MM-DD. Required — null is unresolvable.",
    ),
    deliveryDate: OPTIONAL_DATE_ONLY_INPUT.describe(
      "Expected delivery date, YYYY-MM-DD. Lands on the header (last row wins) AND on this line as its own expected date — an order's lines can carry different ETAs.",
    ),
    // Per LINE, not per order. The header's `orderDate` is one date for the
    // whole order; a source that stamps each line separately is describing
    // something else, and lead time is measured line by line — the ladder
    // reads `receivedDate - orderedDate` off received lines.
    orderedDate: OPTIONAL_DATE_ONLY_INPUT.describe(
      "When THIS LINE was ordered, YYYY-MM-DD. Source-reported, per line — not the header's order date.",
    ),
    receivedDate: OPTIONAL_DATE_ONLY_INPUT.describe(
      "When THIS LINE was received, YYYY-MM-DD. Source-reported; the other half of observed lead time.",
    ),
    // Not a date-only socket: this is an instant, and comparing it against the
    // source's clock is the whole point. Left unwired the column keeps whatever
    // an earlier run established rather than being blanked.
    lastSeenAt: z
      .union([z.string(), z.date()])
      .nullable()
      .optional()
      .describe(
        "When the SOURCE last asserted this order — wire it to the source row's own change timestamp, not to `now`. A full-state export makes a removal an absence rather than an event, so this is what lets a later reconcile tell an order that is still open from one the source stopped sending. `now` would stay fresh for an order the source has dropped.",
      ),
    // Deliberately our vocabulary, not the source's. Decoding '00'/'03'/'CL' is
    // the mapping's job — a step that guessed would bury per-source rules in
    // shared code. A plain string, NOT z.enum, because input validation runs
    // before the handler and is non-retryable: an enum would let ONE unmapped
    // status abort a whole chunk of good rows. The vocabulary check lives in
    // `upsertPurchaseOrdersFromRows`, which reports the row and writes the rest.
    status: z
      .string()
      .nullable()
      .describe(
        "Order status in Gently's `POStatus` vocabulary — e.g. SENT, PARTIALLY_RECEIVED, FULLY_RECEIVED, CLOSED, CANCELLED. Map source codes upstream; anything else is reported per row.",
      ),
    receivedFlag: z
      .boolean()
      .nullable()
      .optional()
      .describe("Whether the order is fully received"),
    totalAmount: QUANTITY_INPUT.nullable()
      .optional()
      .describe(
        "Order total. Leave unwired and the header total is recomputed from the order's persisted lines after each write — correct however the batch chunked.",
      ),
    referenceId: z
      .string()
      .nullable()
      .optional()
      .describe("The source's reference / requisition number"),
    currency: z.string().nullable().optional().describe("ISO currency code"),
    // INTEGER_INPUT, not a bare z.number().int(): ingest foreign-table columns
    // are TEXT, so a socket wired straight to one delivers "3".
    lineNumber: INTEGER_INPUT.nullable()
      .optional()
      .describe("Line number as shown on the order. Display only — not a key."),
    // Not INTEGER_INPUT even though the column is INTEGER: a fractional value
    // here is real data, not a parse error, and failing validation would take
    // the whole chunk down. The service reports it as an invalid row instead.
    quantity: QUANTITY_INPUT.nullable().describe(
      "Ordered quantity. The column is a whole number, so a fractional value is reported as an invalid row rather than rounded.",
    ),
    shippedQuantity: QUANTITY_INPUT.nullable()
      .optional()
      .describe(
        "Quantity already shipped. Open supply is `quantity - shippedQuantity`, so leaving this unwired counts the whole line as still inbound.",
      ),
    unitPrice: QUANTITY_INPUT.nullable()
      .optional()
      .describe(
        "Unit price. Absent lands as 0 — `unit_price` is NOT NULL, and dropping a real line over a missing cost is worse.",
      ),
    description: z
      .string()
      .nullable()
      .optional()
      .describe("Line description. Defaults to the product SKU."),
    unitOfMeasure: z.string().nullable().optional().describe("Unit of measure"),
    lineMetadata: z
      .record(z.string(), z.unknown())
      .nullable()
      .optional()
      .describe("Stored on the line as raw_data"),
    orderMetadata: z
      .record(z.string(), z.unknown())
      .nullable()
      .optional()
      .describe("Stored on the order header as raw_data"),
    // Same contract as upsert_product's changeOperation, with one difference:
    // that sink only refuses to write a deleted row back, while this one ACTS
    // on the delete by closing the still-open line. A delete's change event
    // carries the row's before-image (key columns only), so every other
    // socket is ignored for these rows.
    changeOperation: z
      .enum(["insert", "update", "delete"])
      .optional()
      .describe(
        "CDC operation for this row. Wire from the Observe trigger's Change Operation output. A 'delete' means the source removed the row: the line is closed if still open (header status re-derived, close_reason 'source_removed'), and a line the source already reported terminal keeps that report. Left unwired, every row is an ordinary upsert - and a CDC-fed workflow then writes deleted rows back, so wire it on any CDC path.",
      ),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The order and line were upserted"),
    closedByDelete: z
      .boolean()
      .describe(
        "The row was a CDC delete and its still-open line was closed. False for a delete whose line was already terminal or was never written by this sink (a transfer-order row reaching this step matches nothing, by design).",
      ),
    skipped: z
      .boolean()
      .describe(
        "The row was not written: an unresolvable supplier, or a missing key/date/quantity/status. An unknown product or warehouse alone does not skip a row.",
      ),
    unresolvedSupplier: z
      .boolean()
      .describe("No supplier matched the supplier code"),
    unresolvedProduct: z
      .boolean()
      .describe(
        "The SKU matched NEITHER the legacy product catalog nor the v3 one. The line was still written, without a product reference — check `written`, not this, to know whether it landed.",
      ),
    unresolvedWarehouse: z
      .boolean()
      .describe("A ship-to code was given but matched no warehouse"),
    invalidRow: z
      .boolean()
      .describe(
        "Missing order/line origin id, order date or quantity, an unmapped status, a fractional quantity, or a price/total that is not a number",
      ),
  }),

  configSchema: z.object({
    // Chunks run concurrently unless the definition sets
    // `fanoutStrategy: "sequential"`. Lines are keyed individually and the
    // header aggregate is recomputed from persisted rows, so both are
    // chunk-order-independent — but for a order whose lines SPAN chunks, the
    // header's status, dates and a supplied total are decided by completion order rather
    // than source order.
    //
    // `fanoutStrategy: "sequential"` is the fix. A larger size is NOT one:
    // chunking splits the input at fixed offsets, so a order starting near a
    // boundary straddles it however few lines it has. The only size that helps
    // is one covering the WHOLE input, which collapses the fanout to a single
    // chunk.
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    // Trimmed and non-empty: this value goes verbatim into both natural keys
    // and into reference resolution. A blank or padded one would key rows on
    // "" (or " as400 ") and quietly never match again on the next run.
    originSystem: z
      .string()
      .trim()
      .min(1)
      .describe(
        "Stamped on the order and line, and half of each natural key. Also the default filter for SUPPLIER and WAREHOUSE resolution — override either below when that catalog was ingested under a different value.",
      ),
    // The supplier and warehouse catalogs are routinely stamped differently —
    // at Rexel the vendor master lands as `as400_rexel` and the branch master as
    // `as400_gains` — so one filter would resolve one of them and silently skip
    // every row needing the other. `upsert_supplier_product` carries the same
    // split for the same reason.
    // Trimmed and non-empty for the same reason `originSystem` is: the value
    // becomes an exact equality filter. `??` only falls back for null and
    // undefined, so a persisted "" would survive as an override matching no
    // supplier at all, and a padded " as400_rexel " would match nothing either
    // — both skipping every row while looking configured.
    supplierOriginSystem: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe(
        "Restrict SUPPLIER resolution to references from this origin system. Defaults to `originSystem`.",
      ),
    warehouseOriginSystem: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe(
        "Restrict WAREHOUSE and PRODUCT resolution to references from this origin system. Defaults to `originSystem`.",
      ),
    originEntity: z
      .string()
      .optional()
      .describe(
        "Stamped on the order and its lines for provenance, e.g. the source table name. Not part of any key.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
